import { registerLogoTap } from "../devsheet-hooks";
import type { ComponentChildren } from "preact";
import { href, navigate, type Route } from "../router";
import { useLivePeon, useOriginalAssets, usePeonMoodsUI, useUpgradesUI, useWallpaperUI } from "../features";
import { useWallpaperBackground } from "../wallpaper";
import { usePeonMoodNow } from "../peon-mood-state";
import { LivePeon } from "./LivePeon";
import { PixelPeon } from "./PixelPeon";
import { peonPresentation } from "../peon-art.ts";
import { useSpeech } from "../speech";
import type { PlayingClip } from "../audio";
import { pageAt, paginate, wrapLines } from "../bubble-pages";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";

import { Divider, PixelIcon } from "./PixelArt";
import { tapOpenTab } from "../scroll-to-top";
import { watchTextSize } from "../text-size";
import { MentionText } from "./MentionText";

// Large text (src/text-size.ts): every screen draws this shell, so the watch starts with it.
if (typeof document !== "undefined") {
  if (document.body) watchTextSize();
  else document.addEventListener("DOMContentLoaded", () => watchTextSize(), { once: true });
}

interface HeaderProps {
  title: string;
  backTo?: Route;
  right?: ComponentChildren;
  /**
   * The heading is the peon's speech bubble, not a screen title (owner's ruling, 2026-09-24): his
   * words while he speaks and for a few seconds after, then nothing. `title` is then only the
   * accessible name. Screens whose title is content (a task's name) leave this off: they show the
   * title and give way to the bubble only while a line plays and lingers.
   */
  speech?: boolean;
}

/**
 * `originalAssets` selects the new pixel portrait; `livePeon` selects motion.
 * With motion off, detail/form screens retain just their back button.
 * With motion on, the mascot also sits beside that back button.
 */
/**
 * The peon's words, paged to the bubble: a line longer than two lines of the bubble is shown a page
 * at a time, turning with the speech (src/bubble-pages.ts: progress is proportional to characters,
 * with a small lead). Wrapping is measured with the bubble's own font on a canvas.
 */
export function SpeechBubble({ text, audio, speaking }: { text: string; audio: PlayingClip | null; speaking: boolean }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [pages, setPages] = useState<string[]>([text]);
  const [page, setPage] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const style = getComputedStyle(el);
    const width = el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx || width <= 0) {
      setPages([text]);
      return;
    }
    ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    setPages(paginate(wrapLines(text, (s) => ctx.measureText(s).width <= width), 2));
    setPage(0);
  }, [text]);
  useEffect(() => {
    if (!audio || pages.length < 2) {
      setPage(pages.length - 1 > 0 && !audio ? pages.length - 1 : 0);
      return;
    }
    const follow = () => setPage(pageAt(pages, audio.duration > 0 ? audio.currentTime / audio.duration : 0));
    follow();
    audio.addEventListener("timeupdate", follow);
    return () => audio.removeEventListener("timeupdate", follow);
  }, [audio, pages]);
  return (
    <p ref={ref} class={`speech-bubble${speaking ? " is-speaking" : ""}`} aria-live="polite" aria-label={text}>
      <span class="speech-bubble-text">{pages[Math.min(page, pages.length - 1)]}</span>
    </p>
  );
}

export function Header({ title, backTo, right, speech = false }: HeaderProps) {
  // The household's wallpaper behind every screen (src/wallpaper.ts): the header is drawn once for
  // every route, so the body's texture follows the flag and the board summary from here.
  useWallpaperBackground(useWallpaperUI());
  const { speaking, last, audio } = useSpeech();
  const bubble = speaking ?? last;
  return (
    <header class="topbar">
      {backTo && (
        <a class="topbar-back" href={href(backTo)} aria-label="Back">
          <PixelIcon name="back" />
        </a>
      )}
      <HeaderPeon withBack={!!backTo} />
      {/* The header carries no title text (owner's ruling, 2026-09-24): this space is the peon's
          speech bubble. A pushed screen's own title sits in its content (ScreenTitle, the page's
          h1); a tab has none, so its h1 here is the accessible name only. */}
      <div class="topbar-heading">
        {!backTo && <h1 class="sr-only">{title}</h1>}
        {bubble && <SpeechBubble text={bubble} audio={audio} speaking={speaking !== null} />}
      </div>
      <div class="topbar-right">{right}</div>
    </header>
  );
}

/**
 * A pushed screen's title and divider, at the top of its content rather than in the header (which
 * is the peon's). It is the screen's h1: the header draws none on a pushed screen.
 */
export function ScreenTitle({ title, mentions = false }: { title: string; /** Draw name tags in it (a task's title; docs/mentions.md). */ mentions?: boolean }) {
  return (
    <div class="screen-title">
      <h1 class="screen-title-text">{mentions ? <MentionText text={title} /> : title}</h1>
      <Divider />
    </div>
  );
}

/** Selector for whichever mascot HeaderPeon drew, for anything that needs its place on screen (the tour). */
export const HEADER_PEON_SELECTOR = ".topbar .peon-portrait, .topbar .live-peon, .topbar .topbar-logo";

/**
 * The header's mascot, in whichever form the flags select. Also drawn a second time by the tour
 * (src/components/Tutorial.tsx) over the dimmed screen, so both copies must come from here.
 */
export function HeaderPeon({ withBack = false }: { withBack?: boolean }) {
  const livePeonEnabled = useLivePeon();
  const originalAssets = useOriginalAssets();
  const presentation = peonPresentation(originalAssets, livePeonEnabled);
  // His mood by the board's state (docs/peons.md "Moods"): only the animated pixel portrait has one.
  const act = usePeonMoodNow(usePeonMoodsUI() && presentation === "pixel-animated");
  // The header's peon is the household's assistant, not you (owner, 2026-09-24), so he wears the
  // default shirt and nothing of anyone's. Your peon is the mascot in the account sheet.
  // In a portrait frame (owner's ruling, 2026-09-24): a stone backing and a bronze frame whose bottom
  // edge covers the bust's cut, so he sits in the frame like an RTS unit portrait instead of floating.
  if (presentation === "pixel-animated" || (presentation === "pixel-still" && !withBack)) {
    return (
      <div class="peon-portrait">
        <PixelPeon animated={livePeonEnabled} act={act} />
      </div>
    );
  }
  if (livePeonEnabled) return <LivePeon />;
  if (withBack) return null;
  return <img class="topbar-logo" src="/ui/peon-original@3x.png" alt="" onClick={registerLogoTap} />;
}

interface TabsProps {
  current: Route["name"];
  /**
   * The screen's add button (.fab), drawn inside the tab bar's nav so it sits in a landmark. It is
   * fixed to the window as before, so it still never rides a screen's slide.
   */
  children?: ComponentChildren;
}

export function TabBar({ current, children }: TabsProps) {
  const upgradesUIEnabled = useUpgradesUI();
  const tabs: { route: Route; label: string; icon: "todo" | "done" | "schedules" | "upgrades" }[] = [
    { route: { name: "open" }, label: "To do", icon: "todo" },
    { route: { name: "done" }, label: "Done", icon: "done" },
    { route: { name: "schedules" }, label: "Schedules", icon: "schedules" },
    // Dynasty upgrades (docs/upgrades.md): a fourth tab only while the flag is on.
    ...(upgradesUIEnabled ? [{ route: { name: "upgrades" } as Route, label: "Upgrades", icon: "upgrades" as const }] : []),
  ];
  return (
    <nav class={tabs.length > 3 ? "tabbar tabbar-4" : "tabbar"}>
      {children}
      {tabs.map((t) => (
        <a
          key={t.route.name}
          class={`tab ${current === t.route.name ? "active" : ""}`}
          href={href(t.route)}
          aria-current={current === t.route.name ? "page" : undefined}
          onClick={(e) => {
            // Tabs replace the history entry rather than pushing one (src/back-swipe.ts): back means
            // up a level in this app, never a replay of tab switches.
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            // The tab already open: its list goes back to the top (and a fanned pile stacks), the
            // convention that replaced the back-to-top arrow (2026-10-02 triage, item 8).
            if (current !== t.route.name) navigate(t.route, true);
            else tapOpenTab(t.route.name);
          }}
        >
          <PixelIcon name={t.icon} />
          {/* At large text only the icon shows; the label stays for screen readers (src/text-size.ts). */}
          <span class="tab-label">{t.label}</span>
        </a>
      ))}
    </nav>
  );
}

/** The Profile button in the top bar (owner, 2026-10-01: it reads "Profile", not the name); tapping it opens the account sheet. */
export function WhoButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" class="who" onClick={onClick} title="Profile">
      {/* At large text the button shows its icon and the word is for screen readers only (src/text-size.ts). */}
      <PixelIcon name="profile" className="who-icon" />
      <span class="who-label">Profile</span>
    </button>
  );
}

export function Empty({ children }: { children: ComponentChildren }) {
  return <div class="empty">{children}</div>;
}

/** Loading state: two card-shaped placeholders that pulse, with the label for assistive tech only. */
export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div class="spinner" role="status" aria-live="polite">
      <span class="sr-only">{label}</span>
      <div class="skeleton-card" aria-hidden="true" />
      <div class="skeleton-card" aria-hidden="true" />
    </div>
  );
}

/** An error line; with `onRetry`, a "Try again" link that re-runs the failed load. */
export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div class="form-error" role="alert">
      {message}
      {onRetry && (
        <button type="button" class="error-retry" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}
